import { useEffect, useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";

/* eslint-disable react/no-unescaped-entities */

// ─────────────────────────────────────────────────────────────
// MentionsLegales — Page publique des mentions légales
// Données rendues depuis /api/public/legal (admin-editable).
// ─────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_BACKEND_URL;

export default function MentionsLegales() {
    const [legal, setLegal] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/api/public/legal`)
            .then(({ data }) => setLegal(data.legal))
            .catch((e) =>
                setErr(e?.response?.data?.detail || "Contenu indisponible"),
            );
    }, []);

    return (
        <div data-testid="mentions-legales-page" className="bg-xv-ivory">
            {/* HERO */}
            <section className="bg-xv-midnightDeep text-xv-ivory pt-32 md:pt-40 pb-20 md:pb-24">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-8 text-xv-goldSoft">
                            <span className="block h-px w-12 bg-xv-goldSoft" />
                            <span className="overline">
                                Mentions légales
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-ivory text-[36px] sm:text-5xl lg:text-[60px] leading-[1.04] tracking-tight max-w-3xl"
                            style={{ fontWeight: 300 }}
                        >
                            Informations légales et juridiques.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-9 text-xv-ivory/70 text-[15px] md:text-[16px] leading-[1.9] font-light max-w-2xl">
                            XV Capital Partners est une marque exploitée par
                            XV INVESTISSEMENT HOLDING GROUP, société par actions
                            simplifiée de droit français.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-xv-ivory py-20 md:py-28">
                <div className="max-w-[920px] mx-auto px-6 md:px-12">
                    {err && (
                        <div className="text-[13px] text-red-600 border-l-2 border-red-600 pl-4 py-1 mb-8">
                            {err}
                        </div>
                    )}
                    {!legal ? (
                        <p className="text-[13px] text-xv-text-secondary italic font-light">
                            Chargement…
                        </p>
                    ) : (
                        <article
                            data-testid="mentions-legales-content"
                            className="space-y-14"
                        >
                            <Block title="Éditeur du site">
                                <KV label="Marque" value={legal.brand_name} />
                                <KV label="Société exploitante"
                                    value={`${legal.company_name} (${legal.company_short})`} />
                                <KV label="Forme juridique" value={legal.legal_form} />
                                <KV label="SIREN" value={legal.siren} />
                                <KV label="SIRET du siège" value={legal.siret} />
                                <KV
                                    label="Siège social"
                                    value={
                                        <>
                                            {legal.address_street}<br />
                                            {legal.address_zip} {legal.address_city}<br />
                                            {legal.address_country}
                                        </>
                                    }
                                />
                                <KV label="Date de création" value={legal.created_at_legal} />
                                <KV label="Catégorie d'entreprise" value={legal.category} />
                                <KV label="Activité" value={legal.activity_description} />
                            </Block>

                            <Block title="Directeur de la publication">
                                <p className="text-[15px] font-serif text-xv-black">
                                    {legal.publication_director}
                                </p>
                            </Block>

                            <Block title="Hébergement">
                                {(legal.host_name || legal.host_address || legal.host_contact) ? (
                                    <>
                                        {legal.host_name && <KV label="Nom" value={legal.host_name} />}
                                        {legal.host_address && <KV label="Adresse" value={legal.host_address} />}
                                        {legal.host_contact && <KV label="Contact" value={legal.host_contact} />}
                                    </>
                                ) : (
                                    <p className="text-[14px] text-xv-text-secondary italic font-light">
                                        Informations d'hébergement disponibles sur demande à&nbsp;
                                        <a
                                            href={`mailto:${legal.dpo_email}`}
                                            className="link-underline text-xv-midnight"
                                        >
                                            {legal.dpo_email}
                                        </a>
                                        .
                                    </p>
                                )}
                            </Block>

                            <Block title="Propriété intellectuelle">
                                <Paragraph>
                                    L'ensemble des textes, visuels, logos,
                                    éléments graphiques, bases de données,
                                    concepts, documents et contenus présents
                                    sur le site sont protégés par le Code de
                                    la propriété intellectuelle.
                                </Paragraph>
                                <Paragraph>
                                    Toute reproduction, représentation,
                                    diffusion ou exploitation, totale ou
                                    partielle, sans autorisation écrite
                                    préalable est interdite.
                                </Paragraph>
                            </Block>

                            <Block title="Responsabilité">
                                <Paragraph>
                                    Les informations publiées sur le site sont
                                    fournies à titre informatif et ne
                                    constituent ni une offre contractuelle, ni
                                    un conseil juridique, fiscal, comptable ou
                                    financier personnalisé.
                                </Paragraph>
                                <Paragraph>
                                    {legal.brand_name} intervient comme cabinet
                                    de conseil stratégique et de structuration
                                    et coordonne, lorsque cela est nécessaire,
                                    les expertises adaptées aux besoins de
                                    ses clients.
                                </Paragraph>
                            </Block>

                            <Block title="Activité">
                                <Paragraph>
                                    Le site présente les activités d'accompagnement
                                    stratégique, de structuration d'opérations,
                                    d'ingénierie financière, de développement
                                    d'entreprise, de transmission, de
                                    coordination d'expertises et
                                    d'accompagnement patrimonial.
                                </Paragraph>
                                <Paragraph>
                                    {legal.brand_name} n'est ni une banque, ni
                                    un établissement de crédit, ni un
                                    prestataire de services d'investissement,
                                    et ne collecte directement aucune épargne
                                    du public.
                                </Paragraph>
                            </Block>

                            <Block title="Liens hypertextes">
                                <Paragraph>
                                    Les liens externes présents sur le site
                                    sont fournis à titre informatif.
                                    {" "}
                                    {legal.brand_name} ne saurait être
                                    responsable du contenu des sites tiers.
                                </Paragraph>
                            </Block>

                            <Block title="Droit applicable">
                                <Paragraph>
                                    Le présent site est soumis au droit français.
                                </Paragraph>
                                <Paragraph>
                                    Tout litige relatif à son utilisation
                                    relève de la compétence des juridictions
                                    françaises compétentes.
                                </Paragraph>
                            </Block>
                        </article>
                    )}
                </div>
            </section>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <Reveal>
            <section>
                <h2
                    className="font-serif text-xv-black text-[24px] md:text-[28px] leading-tight mb-7 pb-4 border-b border-xv-line"
                    style={{ fontWeight: 400 }}
                >
                    {title}
                </h2>
                <div className="space-y-5">{children}</div>
            </section>
        </Reveal>
    );
}

function KV({ label, value }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 md:gap-8 items-baseline">
            <span className="text-[11px] tracking-[0.24em] uppercase text-xv-gold/85">
                {label}
            </span>
            <span className="font-serif text-[15px] text-xv-black leading-relaxed">
                {value || "—"}
            </span>
        </div>
    );
}

function Paragraph({ children }) {
    return (
        <p className="text-[15px] md:text-[16px] text-xv-text-secondary leading-[1.9] font-light">
            {children}
        </p>
    );
}
